// 📁 frontend/src/pages/AdminDeliveryZonesPage.jsx
import React, { useEffect, useState, useCallback } from 'react';
import {
  addDeliveryZone,
  deleteDeliveryZone,
  updateDeliveryZone,
  fetchDeliveryZones
} from '../api/api';

export default function AdminDeliveryZonesPage() {
  const [zones, setZones] = useState([]);
  const [form, setForm] = useState({ name: '', delivery_fee: '', delivery_time: '' });
  const [editingId, setEditingId] = useState(null);
  const [toastMessage, setToastMessage] = useState('');

  const showToast = (message) => {
    setToastMessage(message);
    setTimeout(() => setToastMessage(''), 2500);
  };

  const loadZones = useCallback(async () => {
    try {
      const data = await fetchDeliveryZones();
      setZones(data);
    } catch (error) {
      showToast('❌ فشل في جلب البيانات');
    }
  }, []);

  useEffect(() => {
    loadZones();
  }, [loadZones]);

  function handleChange(e) {
    setForm({ ...form, [e.target.name]: e.target.value });
  }

  async function handleSubmit(e) {
    e.preventDefault();
    try {
      if (editingId) {
        await updateDeliveryZone(editingId, form);
        showToast('✅ تم تعديل المنطقة بنجاح');
      } else {
        await addDeliveryZone(form);
        showToast('✅ تم إضافة المنطقة بنجاح');
      }
      setForm({ name: '', delivery_fee: '', delivery_time: '' });
      setEditingId(null);
      loadZones();
    } catch (error) {
      showToast('❌ حدث خطأ أثناء الحفظ');
    }
  }

  async function handleDelete(id) {
    if (!window.confirm('⚠️ هل أنت متأكد من الحذف؟')) return;
    try {
      await deleteDeliveryZone(id);
      showToast('✅ تم الحذف بنجاح');
      loadZones();
    } catch {
      showToast('❌ فشل في الحذف');
    }
  }

  return (
    <div style={styles.container}>
      {/* ✅ Toast */}
      {toastMessage && <div style={styles.toast}>{toastMessage}</div>}

      {/* ✅ Header */}
      <header style={styles.header}>
        <h1 style={styles.pageTitle}>🚚 إدارة مناطق التوصيل</h1>
      </header>

      {/* ✅ Summary Card */}
      <div style={styles.summaryCard}>
        <p style={styles.summaryValue}>{zones.length}</p>
        <p style={styles.summaryLabel}>📊 إجمالي المناطق المسجلة</p>
      </div>

      {/* ✅ Form */}
      <div style={styles.card}>
        <h3 style={styles.cardTitle}>
          {editingId ? '✏️ تعديل المنطقة' : '➕ إضافة منطقة جديدة'}
        </h3>
        <form onSubmit={handleSubmit} style={styles.form}>
          <input
            type="text"
            name="name"
            placeholder="🏙️ اسم المنطقة"
            value={form.name}
            onChange={handleChange}
            required
            style={styles.input}
          />
          <input
            type="number"
            name="delivery_fee"
            placeholder="💰 رسوم التوصيل (₪)"
            value={form.delivery_fee}
            onChange={handleChange}
            required
            style={styles.input}
          />
          <input
            type="text"
            name="delivery_time"
            placeholder="⏱️ زمن التوصيل المتوقع"
            value={form.delivery_time}
            onChange={handleChange}
            required
            style={styles.input}
          />
          <button
            type="submit"
            style={{
              ...styles.submitButton,
              backgroundColor: editingId ? '#3390FF' : '#47AA52'
            }}
          >
            {editingId ? '💾 تحديث المنطقة' : '➕ حفظ المنطقة'}
          </button>
        </form>
      </div>

      {/* ✅ Table List */}
      <div style={styles.tableWrapper}>
        <h3 style={styles.sectionTitle}>📋 قائمة المناطق</h3>
        <table style={styles.table}>
          <thead style={styles.tableHead}>
            <tr>
              <th style={styles.th}>📍 الاسم</th>
              <th style={styles.th}>💰 الرسوم (₪)</th>
              <th style={styles.th}>⏱️ الزمن</th>
              <th style={styles.th}>⚙️ الإجراءات</th>
            </tr>
          </thead>
          <tbody>
            {zones.length > 0 ? (
              zones.map((zone) => (
                <tr key={zone.id} style={styles.tr}>
                  <td style={styles.td}>{zone.name}</td>
                  <td style={styles.td}>{zone.delivery_fee} ₪</td>
                  <td style={styles.td}>{zone.delivery_time}</td>
                  <td style={styles.td}>
                    <button
                      onClick={() => setEditingId(zone.id) || setForm(zone)}
                      style={styles.editButton}
                    >
                      ✏️ تعديل
                    </button>
                    <button
                      onClick={() => handleDelete(zone.id)}
                      style={styles.deleteButton}
                    >
                      🗑️ حذف
                    </button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={4} style={styles.emptyRow}>🚫 لا توجد مناطق مسجلة بعد</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* 🎨 Styles */
const styles = {
  container: {
    minHeight: '100vh',
    padding: '40px 20px',
    backgroundColor: '#FFF8F0',
    fontFamily: "'Cairo', sans-serif",
    direction: 'rtl'
  },
  header: {
    textAlign: 'center',
    marginBottom: '25px'
  },
  pageTitle: {
    fontSize: '26px',
    fontWeight: 800,
    color: '#4A3527'
  },
  toast: {
    position: 'fixed',
    top: '24px',
    left: '50%',
    transform: 'translateX(-50%)',
    zIndex: 999,
    padding: '10px 18px',
    backgroundColor: '#4BB543',
    color: '#FFF',
    borderRadius: '8px',
    fontSize: '14px',
    boxShadow: '0 5px 10px rgba(0,0,0,0.1)'
  },
  summaryCard: {
    textAlign: 'center',
    backgroundColor: '#FFF4E1',
    padding: '25px 15px',
    borderRadius: '15px',
    marginBottom: '30px',
    boxShadow: '0 6px 15px rgba(0,0,0,0.08)',
    border: '1px solid #F4DCC3',
    maxWidth: '300px',
    margin: '0 auto 30px'
  },
  summaryValue: {
    fontSize: '40px',
    fontWeight: 700,
    color: '#4A281A'
  },
  summaryLabel: {
    color: '#A06B47',
    fontSize: '16px'
  },
  card: {
    backgroundColor: '#FFF',
    padding: '20px',
    borderRadius: '12px',
    maxWidth: '500px',
    margin: '0 auto 30px',
    boxShadow: '0 6px 15px rgba(0,0,0,0.06)',
    border: '1px solid #E7D8C9'
  },
  cardTitle: {
    fontSize: '18px',
    fontWeight: 700,
    color: '#583B28',
    marginBottom: '20px'
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '14px'
  },
  input: {
    padding: '12px 15px',
    borderRadius: '10px',
    border: '1px solid #DCC6B0',
    fontSize: '14px'
  },
  submitButton: {
    padding: '12px',
    borderRadius: '10px',
    border: 'none',
    color: '#FFF',
    fontWeight: '700',
    cursor: 'pointer',
    fontSize: '15px'
  },
  tableWrapper: {
    marginTop: '15px'
  },
  sectionTitle: {
    fontSize: '18px',
    fontWeight: '700',
    color: '#654833',
    textAlign: 'center',
    marginBottom: '15px'
  },
  table: {
    width: '100%',
    borderRadius: '10px',
    backgroundColor: '#FFF',
    boxShadow: '0 6px 12px rgba(0,0,0,0.05)',
    border: '1px solid #E6D6C5',
    overflow: 'hidden'
  },
  tableHead: {
    backgroundColor: '#F2E7DB'
  },
  th: {
    padding: '14px',
    textAlign: 'right',
    color: '#6B4E3A',
    fontSize: '14px'
  },
  tr: {
    borderBottom: '1px solid #EFE1D4'
  },
  td: {
    padding: '12px',
    fontSize: '14px',
    color: '#4B3D34'
  },
  editButton: {
    backgroundColor: '#6AB6FF',
    color: '#FFF',
    padding: '6px 12px',
    borderRadius: '8px',
    border: 'none',
    margin: '0 4px',
    cursor: 'pointer'
  },
  deleteButton: {
    backgroundColor: '#FF7272',
    color: '#FFF',
    padding: '6px 12px',
    borderRadius: '8px',
    border: 'none',
    margin: '0 4px',
    cursor: 'pointer'
  },
  emptyRow: {
    padding: '20px',
    textAlign: 'center',
    color: '#8F8F8F'
  }
};

